import { Component, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/AdminService/admin';
import { AuthService } from '../../services/AuthService/auth';
import { CompanyResponse, UserResponse } from '../../models/company.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { TopUpDialog } from '../../dialogs/top-up-dialog/top-up-dialog';
import { ChangeRoleDialog } from '../../dialogs/change-role-dialog/change-role-dialog';
import { DisableUserDialog } from '../../dialogs/disable-user-dialog/disable-user-dialog';

@Component({
  selector: 'app-company-detail',
  imports: [CommonModule, StatusBadge, ErrorBanner, EmptyState],
  templateUrl: './company-detail.html',
  styleUrl: './company-detail.scss',
})
export class CompanyDetail implements OnInit {
  companyId = '';
  company = signal<CompanyResponse | null>(null);
  users = signal<UserResponse[]>([]);

  isLoading = signal(false);
  errorMessage = signal('');
  notFound = signal(false);

  togglingUserId = signal<string | null>(null);

  constructor(
    private route: ActivatedRoute,
    private admin: AdminService,
    private auth: AuthService,
    private dialog: MatDialog,
    private location: Location
  ) {}

  goBack(): void {
    this.location.back();
  }

  get canManageUsers(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  get canTopUpWallet(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  get isCompanyLocked(): boolean {
    const status = this.company()?.status;
    return status === 'PENDING' || status === 'REJECTED';
  }

  get toggleTooltip(): string {
    const status = this.company()?.status;
    return this.isCompanyLocked
      ? `Company registration is ${status} — accounts can't log in regardless of this setting`
      : '';
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.notFound.set(false);

    this.admin.getCompany(this.companyId).subscribe({
      next: (res) => {
        this.company.set(res.data);
        this.loadUsers();
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        if (err.status === 404) {
          this.notFound.set(true);
          return;
        }
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load company.'));
      },
    });
  }

  private loadUsers(): void {
    this.admin.listCompanyUsers(this.companyId).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.users.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load company users.'));
      },
    });
  }

  openTopUpDialog(): void {
    const c = this.company();
    if (!c) {
      return;
    }
    this.dialog
      .open(TopUpDialog, { width: '420px', data: { companyId: c.id, companyName: c.name } })
      .afterClosed()
      .subscribe();
  }

  openChangeRoleDialog(user: UserResponse): void {
    this.dialog
      .open(ChangeRoleDialog, {
        width: '480px',
        maxHeight: '90vh',
        data: { userId: user.id, userEmail: user.email, currentRole: user.role },
      })
      .afterClosed()
      .subscribe((changed) => {
        if (changed) {
          this.loadUsers();
        }
      });
  }

  toggleUser(user: UserResponse): void {
    if (this.isCompanyLocked) {
      return;
    }

    if (user.enabled) {
      this.openDisableDialog(user);
      return;
    }

    this.togglingUserId.set(user.id);
    this.admin.enableUser(user.id).subscribe({
      next: () => {
        this.togglingUserId.set(null);
        this.loadUsers();
      },
      error: (err: HttpErrorResponse) => {
        this.togglingUserId.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to update user.'));
      },
    });
  }

  private openDisableDialog(user: UserResponse): void {
    this.dialog
      .open(DisableUserDialog, {
        width: '480px',
        data: { userId: user.id, userEmail: user.email },
      })
      .afterClosed()
      .subscribe((disabled) => {
        if (disabled) {
          this.loadUsers();
        }
      });
  }
}
